import React, { useRef, useEffect } from "react";
import { BryntumWidget } from "@bryntum/schedulerpro-react";
import { pluralize } from "@/other/utils/otherUtils";
import { WIDGET_KEYS } from "@/other/constants/WidgetKeys";

const customGrouperRenderer = ({ groupRowFor, count, isFirstColumn, record }: any) => {
  // groupColumn can be missing, so we cannot rely on it
  // Check group field in the group record
  if (record.meta.groupField === "status") {
    if (isFirstColumn) {
      const createMsg = `Successfully Created (${count}) ${pluralize(count, "Record")}`;
      const updateMsg = `Successfully Updated (${count}) ${pluralize(count, "Record")}`;
      const error = `❌ Error In Importing (${count}) ${pluralize(count, "Record")}`;
      const displayMsg =
        groupRowFor === "error" ? error : groupRowFor === "updated" ? updateMsg : createMsg;
      return `<span style="font-weight: bold">${displayMsg}</span>`;
    }
  }
  return "";
};

interface ImportFeedbackTabProps {
  importResponseData: {
    rows?: any[];
    created_count?: number;
    error_count?: number;
    updated_count?: number;
  } | null;
  labelIndex: number;
}

export const ImportFeedbackTab: React.FC<ImportFeedbackTabProps> = ({
  importResponseData,
  labelIndex
}) => {
  const gridRef = useRef<any>(null);
  const totalCountDisplayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!importResponseData || !gridRef.current?.instance) return;

    const { rows: data, created_count = 0, error_count = 0, updated_count = 0 } = importResponseData;
    const grid = gridRef.current.instance;

    // Calculate total records count
    const totalRecordsCount = created_count + error_count + updated_count;

    // Update total count display
    if (totalCountDisplayRef.current) {
      totalCountDisplayRef.current.textContent = `Total ${pluralize(totalRecordsCount, "Record")}: ${totalRecordsCount}`;
    }

    // Check if data exists and is an array before processing
    if (!data || !Array.isArray(data)) {
      grid.data = [];
      return;
    }

    // Since bryntum creates record template using the first row in the record and since
    // the response from backend is not consistent (doesn't always include `message` field),
    // we ensure all rows have a message field
    const processedData = data.map((d: any) => ({
      ...d,
      message: d.message || `Successfully imported: "${d.data?.[labelIndex] || ""}"`
    }));

    grid.data = processedData;
    grid.collapseAll();
  }, [importResponseData, labelIndex]);

  const gridConfig = {
    height: "100%",
    emptyText: "No records were imported. The file may be empty or contain invalid data.",
    features: {
      cellEdit: false,
      headerMenu: false,
      sort: { multiSort: false },
      group: {
        field: "status",
        renderer: customGrouperRenderer
      }
    },
    columns: [
      {
        id: "import-feedback-row-idx",
        field: "row_index",
        text: "Row #",
        groupable: false
      },
      {
        id: "import-feedback-status",
        field: "status",
        text: "Status",
        hidden: true
      },
      {
        id: "import-feedback-message",
        field: "message",
        text: "Message",
        groupable: false,
        flex: 1
      }
    ]
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-hidden">
        {/* @ts-expect-error bryntum widget throws error on passing config as a prop */}
        <BryntumWidget ref={gridRef} type={WIDGET_KEYS.swivuPreferenceStyleGrid} config={gridConfig} />
      </div>

      <div className="flex items-center justify-start p-4 border-t border-border">
        <div ref={totalCountDisplayRef} className="text-sm font-medium">
          Total Records: 0
        </div>
      </div>
    </div>
  );
};